/**
 * FeedbackTable — dense sortable table view for the Feedback tab.
 *
 * Replaces the v1.3 `<FeedbackCardList>` (vertical stack of cards) after the
 * user report that the card list "is not usable" for triaging feedback at
 * scale (~1.5k-row windows). A table packs 10× more rows into the viewport
 * and lets the reviewer scan by column rather than read each card top-to-
 * bottom.
 *
 * Phase 5 (dashboard-trace-ux):
 *   - Row expand is now MULTI-row + DEEP-LINKABLE. The set of expanded trace
 *     IDs is owned by the page (`useFeedbackUrlState.expanded`), serialized
 *     as `?expanded=tid_a,tid_b` (capped at 10) and survives reload.
 *   - The expanded sub-row hosts `<TraceDetailPanel variant="inline">`
 *     directly (markdown via react-markdown + remark-gfm + Tailwind prose),
 *     replacing the `<FeedbackCard expanded />` indirection. The previous
 *     mobile-only `<Dialog>` branch is gone — inline expand is the only path
 *     on every viewport. The desktop colspan was already wide; mobile gets
 *     the full table-width slot and scrolls horizontally if needed.
 *   - Collapsed rows now show server-truncated `inputPreview` /
 *     `outputPreview` snippets (FeedbackCardRow.{input,output}Preview, set
 *     by the backend's `toPreview` — see queries.ts) so reviewers can scan
 *     without expanding.
 *   - A chevron in the rightmost column gives a visual toggle hint and
 *     announces `aria-expanded` on the underlying button.
 *   - Keyboard a11y: Enter / Space on a row toggles its expand. Esc inside
 *     an expanded panel collapses that one row.
 *
 * Column set (IA F-04) — after BUG-3 the Context column is gone (the chip
 * was low-signal and ate width; `contextType` is still available inside
 * the expand-row detail panel). Latency and Cost are sortable:
 *   1. Time       — relative ("2 h ago") + tooltip with absolute ISO.
 *   2. Name       — directory-joined display name + (mobile) preview.
 *   3. User       — react-router link to /user/:userId.
 *   4. Rating     — star glyph + "n/5"; em-dash for text-only (null rating).
 *                   Low ratings (1 / 2) rendered in destructive tint so
 *                   triage-at-a-glance works.
 *   5. Feedback   — preview of textFeedback + Phase 5 input/output preview
 *                   snippets (1-2 lines each, server-truncated).
 *   6. Latency    — ms / s formatting. Sortable (asc / desc).
 *   7. Cost       — USD or em-dash. Sortable (asc / desc).
 *   8. Brief?     — small badge when isBrief=true.
 *   9. Toggle     — chevron + external Langfuse link.
 *
 * Sort contract:
 *   The server exposes eight canonical sort modes (`triage`, `newest`,
 *   `oldest`, `best`, `latency_asc`, `latency_desc`, `cost_asc`,
 *   `cost_desc`). We map the four "bulk" columns onto that enum:
 *     - Time    header → toggle newest ⇄ oldest
 *     - Rating  header → toggle triage ⇄ best
 *     - Latency header → toggle latency_desc ⇄ latency_asc (desc first,
 *                        slowest-first is the triage-useful direction)
 *     - Cost    header → toggle cost_desc ⇄ cost_asc (desc first,
 *                        most-expensive-first is the triage-useful direction)
 *   Click handlers live in the owning page via `onSortChange`. Non-sortable
 *   columns (name / user / brief / toggle) render a plain label.
 *
 * Selectors:
 *   - `data-testid="feedback-table"` on the root wrapper (audit probe).
 *   - `data-testid="feedback-row"` on each data <tr>.
 *   - `data-testid="feedback-row-expanded"` on each open detail row.
 *   - `data-testid="feedback-header-<col>"` on clickable sort headers.
 */

import * as React from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { formatLatency, formatTraceCost } from '@/lib/format';
import { UserLabel } from '@/components/ui/user-label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TraceDetailPanel } from '@/components/trace/TraceDetailPanel';
import { useFeedbackTraceDetail } from '@/hooks/use-feedback-trace-detail';
import {
  formatDateTimeFull,
  formatRelativeCet,
} from '@/lib/format-date';
import { langfuseTraceUrl } from '@/lib/langfuse';
import { cn } from '@/lib/utils';
import type { FeedbackCardRow, FeedbackSort } from '@/services/api-types';

// ---------------------------------------------------------------------------
// Row-scoped error boundary
// ---------------------------------------------------------------------------
//
// BUG-C (v1.4 dashboard-ux-fix): inline row-expand error boundary.
//
// The user report was: expanding a row that can't load its Langfuse trace
// content renders "Failed to load trace content. Retry", and afterwards the
// global FilterBar chips stop responding. Root cause: a render-time throw
// inside the trace-detail subtree (the Langfuse fetch can return unexpected
// shapes that blow up downstream) bubbled past the React tree around
// <FeedbackTable>. The nearest ancestor boundary was the page-level
// <TabErrorBoundary name="feedback">, which swaps the WHOLE table for a
// reload-prompt card — but <FilterBar> is a SIBLING of <Outlet/> in
// DashboardLayout, so the boundary replacing the outlet's subtree leaves
// the filter bar in place yet kills the event wiring (the old handlers on
// the destroyed Outlet subtree are gone; the new boundary card doesn't
// re-subscribe anything). To the user that reads as "filters stopped
// toggling".
//
// Fix: wrap the expand <TableRow>'s content in a row-scoped class boundary
// so ANY render error inside the trace-detail subtree stays CONFINED to
// that one row. No URL writes, no filter mutations, no parent re-renders.
// The collapsed row and the whole table around it keep working; the user
// can close the broken row and continue triaging. The boundary resets its
// error state as soon as a different row is expanded (the traceId prop
// changes), so a transient Langfuse 5xx on one row doesn't poison the
// component for the next.
interface RowExpandErrorBoundaryProps {
  traceId: string;
  onClose: () => void;
  children: React.ReactNode;
}
interface RowExpandErrorBoundaryState {
  error: Error | null;
}
class RowExpandErrorBoundary extends React.Component<
  RowExpandErrorBoundaryProps,
  RowExpandErrorBoundaryState
> {
  state: RowExpandErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RowExpandErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      '[FeedbackTable:RowExpand]',
      this.props.traceId,
      error,
      info.componentStack,
    );
  }

  componentDidUpdate(prevProps: RowExpandErrorBoundaryProps): void {
    if (prevProps.traceId !== this.props.traceId && this.state.error) {
      this.setState({ error: null });
    }
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div
          data-testid="feedback-row-expand-error"
          className="text-sm space-y-2"
        >
          <div className="font-medium text-red-600">
            Couldn&apos;t render trace detail.
          </div>
          <div className="text-xs text-muted-foreground">
            {this.state.error.message || 'Unexpected render error.'}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={this.props.onClose}
            data-testid="feedback-row-expand-error-close"
          >
            Close
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Lazy detail wrapper — fetches once on mount, hands the result to TraceDetailPanel
// ---------------------------------------------------------------------------

/**
 * RowExpandPanel — owns the on-expand `/feedback/trace-detail` fetch for one
 * trace and adapts the response into the shape `<TraceDetailPanel>` expects.
 *
 * The panel is presentation-only (it takes a fully-resolved `detail` prop),
 * so we wrap it in this thin fetch shell. The hook is keyed on `traceId` and
 * has a 5-min staleTime, so collapsing + re-expanding a row never refetches.
 *
 * On error we render an inline RED retry surface (per Phase 5 spec —
 * "if trace expand fetch fails, show inline error in expand area, do not
 * collapse the row silently"). The Retry button calls the hook's `refetch`.
 */
interface RowExpandPanelProps {
  traceId: string;
  feedback: { rating: number | null; text: string | null } | null;
  /**
   * Optional fallbacks shown while the network is in flight — server-side
   * previews from `FeedbackCardRow`. Lets the panel reveal something
   * immediately instead of an empty skeleton.
   */
  inputPreview?: string | null;
  outputPreview?: string | null;
  /** Trace metadata available from the row — lets the header fill out
   *  before the network resolves. */
  timestamp?: string | null;
  durationMs?: number | null;
  cost?: number | null;
  /** True for morning-brief rows (`row.isBrief`) — renders the full delivered
   *  brief instead of the "sent N messages" status string. */
  isBrief?: boolean;
}

function RowExpandPanel({
  traceId,
  feedback,
  inputPreview,
  outputPreview,
  timestamp,
  durationMs,
  cost,
  isBrief,
}: RowExpandPanelProps): React.ReactElement {
  const detail = useFeedbackTraceDetail(traceId, true);

  if (detail.isLoading) {
    return (
      <div
        className="space-y-2"
        data-testid="feedback-row-expand-loading"
      >
        <div className="h-3 w-24 rounded bg-muted animate-pulse" />
        <div className="h-3 w-full rounded bg-muted animate-pulse" />
        <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
        <div className="h-3 w-20 rounded bg-muted animate-pulse mt-2" />
        <div className="h-3 w-full rounded bg-muted animate-pulse" />
      </div>
    );
  }

  if (detail.isError) {
    return (
      <div
        className="space-y-2 text-sm"
        data-testid="feedback-row-expand-fetch-error"
      >
        <div className="font-medium text-red-600">
          Failed to load trace detail.
        </div>
        <div className="text-xs text-muted-foreground">
          The Langfuse fetch did not return — the trace may have rotated out
          of retention, or the upstream is unreachable.
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void detail.refetch()}
          data-testid="feedback-row-expand-retry"
        >
          Retry
        </Button>
      </div>
    );
  }

  // Compose a TraceDetail object out of the row metadata + on-expand
  // detail fetch. When the fetch returned no input/output we fall back to
  // the server-side preview from the row so reviewers always see SOME
  // text instead of an empty card.
  const data = detail.data;
  return (
    <TraceDetailPanel
      variant="inline"
      detail={{
        traceId,
        timestamp: timestamp ?? null,
        durationMs: durationMs ?? null,
        cost: cost ?? null,
        input: data?.input ?? inputPreview ?? null,
        output: data?.output ?? outputPreview ?? null,
        // The blocks the CODE drew beside the answer — the connect card. No preview
        // fallback: the mirror row carries no such snippet, so the section simply
        // appears when the on-expand fetch lands.
        codeDrawn: data?.codeDrawn ?? null,
        error: data?.error ?? null,
      }}
      feedback={feedback}
      isBrief={isBrief}
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SortColumn = 'time' | 'rating' | 'latency' | 'cost';

export interface FeedbackTableProps {
  rows: FeedbackCardRow[];
  /** COUNT(*) over the same predicate on the backend — drives the
   *  "Showing N of M" counter and the Load-more disabled state. */
  total: number;
  /** Currently committed server sort (from useFeedbackUrlState). */
  sort: FeedbackSort;
  /** Called when a sortable header is clicked. The owning page should
   *  commit the new sort and reset offset to 0 so the fresh page reflects
   *  the new order. */
  onSortChange: (next: FeedbackSort) => void;
  /** First-load spinner trigger. Ignored once rows are present. */
  isLoading: boolean;
  /** True while the NEXT page is in flight — spinner on Load-more. */
  isFetchingMore: boolean;
  onLoadMore: () => void;
  /** Russian-by-default to match WARNING-10 wording. */
  emptyLabel?: string;
  /**
   * Phase 5 — controlled set of trace IDs whose detail panels are
   * inline-expanded. Owned by the page via `useFeedbackUrlState.expanded`
   * so the URL is the single source of truth and deep-links reproduce the
   * user's expanded layout on reload.
   */
  expandedTraceIds: string[];
  /**
   * Phase 5 — called when the user toggles a row. The page commits the
   * new array to the URL via `setExpanded` (capped + deduped at the
   * hook). The component itself does not enforce the cap; it just emits
   * the requested set.
   */
  onExpandedChange: (next: string[]) => void;
}

/**
 * P3-10 (v1.4-dashboard-ssot): Radix tooltip wrapper that replaces the
 * native `title=""` tooltip used on the feedback-preview cell. `title` is
 * mouse-hover-only — keyboard and touch users never saw the full text.
 * Radix opens on focus as well, and `TooltipContent` is auto-portaled
 * through `ui/tooltip.tsx`, so the rendered tip escapes the cell's tight
 * width + overflow constraints.
 */
function FeedbackTextPreview({
  text,
}: {
  text: string;
}): React.ReactElement {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="line-clamp-2 text-foreground cursor-help"
            tabIndex={0}
          >
            {text}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-md whitespace-pre-wrap text-left"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function relativeTime(now: Date, then: Date): string {
  return formatRelativeCet(then, now);
}


/** Build a star string (★ filled + ☆ empty) for a 1–5 rating. Null → em-dash. */
function formatStars(rating: number | null): string {
  if (rating == null) return '—';
  const filled = Math.max(0, Math.min(5, Math.round(rating)));
  return '★'.repeat(filled) + '☆'.repeat(5 - filled);
}

function arrowFor(column: SortColumn, sort: FeedbackSort): '▲' | '▼' | '' {
  if (column === 'time') {
    if (sort === 'newest') return '▼';
    if (sort === 'oldest') return '▲';
    return '';
  }
  if (column === 'rating') {
    if (sort === 'best') return '▼';
    if (sort === 'triage') return '▲';
    return '';
  }
  if (column === 'latency') {
    if (sort === 'latency_desc') return '▼';
    if (sort === 'latency_asc') return '▲';
    return '';
  }
  if (column === 'cost') {
    if (sort === 'cost_desc') return '▼';
    if (sort === 'cost_asc') return '▲';
    return '';
  }
  return '';
}

function ariaSortFor(
  column: SortColumn,
  sort: FeedbackSort,
): 'ascending' | 'descending' | 'none' {
  const arrow = arrowFor(column, sort);
  if (arrow === '▲') return 'ascending';
  if (arrow === '▼') return 'descending';
  return 'none';
}

function nextSort(column: SortColumn, current: FeedbackSort): FeedbackSort {
  if (column === 'time') {
    if (current === 'newest') return 'oldest';
    return 'newest';
  }
  if (column === 'rating') {
    if (current === 'triage') return 'best';
    return 'triage';
  }
  if (column === 'latency') {
    if (current === 'latency_desc') return 'latency_asc';
    return 'latency_desc';
  }
  if (current === 'cost_desc') return 'cost_asc';
  return 'cost_desc';
}

function SortHeader({
  column,
  label,
  sort,
  onSortChange,
  align = 'left',
}: {
  column: SortColumn;
  label: string;
  sort: FeedbackSort;
  onSortChange: (next: FeedbackSort) => void;
  align?: 'left' | 'right';
}): React.ReactElement {
  const arrow = arrowFor(column, sort);
  return (
    <button
      type="button"
      onClick={() => onSortChange(nextSort(column, sort))}
      data-testid={`feedback-header-${column}`}
      data-sort-dir={arrow === '▲' ? 'asc' : arrow === '▼' ? 'desc' : 'none'}
      className={cn(
        'inline-flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground transition-colors',
        align === 'right' && 'justify-end text-right',
      )}
    >
      {label}
      <span
        aria-hidden="true"
        className="ml-0.5 inline-block w-3 text-center text-[10px] font-normal"
      >
        {arrow}
      </span>
    </button>
  );
}

/** Sized to what it replaces: this table accumulates pages, so the loaded
 *  body is routinely 50-150 rows and a fixed six collapsed the page by a
 *  screen or more on every filter change. `SKELETON_ROWS` is the cold-tab
 *  fallback only. */
/** Cold-tab fallback only: rows to show when this table has never had any. */
const SKELETON_ROWS = 6;

function LoadingSkeleton({
  colCount,
  rows,
}: {
  colCount: number;
  rows: number;
}): React.ReactElement {
  return (
    <TableBody data-testid="feedback-table-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: colCount }).map((__, c) => (
            <TableCell key={c}>
              <div className="h-4 rounded bg-muted animate-pulse" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

/** Compact 1-2 line preview line shown ONLY in the collapsed row's Feedback
 *  cell. Server-truncated upstream — we just clamp lines + colorize the label
 *  so input/output are visually distinct on a fast scan. */
function PreviewLine({
  label,
  text,
}: {
  label: 'in' | 'out';
  text: string;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 items-baseline gap-1.5 leading-snug">
      <span
        aria-hidden="true"
        className={cn(
          'shrink-0 font-semibold tabular-nums text-[10px] uppercase tracking-wide',
          label === 'in'
            ? 'text-sky-600 dark:text-sky-400'
            : 'text-emerald-600 dark:text-emerald-400',
        )}
      >
        {label}
      </span>
      <span
        className="line-clamp-2 min-w-0 flex-1 break-words text-foreground/90"
        data-testid={`feedback-row-${label}-preview`}
      >
        {text}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeedbackTable
// ---------------------------------------------------------------------------

export function FeedbackTable({
  rows,
  total,
  sort,
  onSortChange,
  isLoading,
  isFetchingMore,
  onLoadMore,
  emptyLabel = 'No data',
  expandedTraceIds,
  onExpandedChange,
}: FeedbackTableProps): React.ReactElement {
  // Memoise the Set of expanded IDs so per-row checks stay O(1) and we
  // don't trigger unrelated re-renders on identity changes.
  const expandedSet = React.useMemo(
    () => new Set(expandedTraceIds),
    [expandedTraceIds],
  );

  const toggleRow = React.useCallback(
    (traceId: string) => {
      const next = expandedSet.has(traceId)
        ? expandedTraceIds.filter((id) => id !== traceId)
        : [...expandedTraceIds, traceId];
      onExpandedChange(next);
    },
    [expandedSet, expandedTraceIds, onExpandedChange],
  );

  const collapseRow = React.useCallback(
    (traceId: string) => {
      if (!expandedSet.has(traceId)) return;
      onExpandedChange(expandedTraceIds.filter((id) => id !== traceId));
    },
    [expandedSet, expandedTraceIds, onExpandedChange],
  );

  /* How many rows this table last showed — see LoadingSkeleton. Written after
     the render that showed them, so the render that blanks them cannot
     overwrite it. */
  const lastRowCount = React.useRef(SKELETON_ROWS);
  React.useEffect(() => {
    if (rows.length > 0) lastRowCount.current = rows.length;
  }, [rows.length]);
  const skeletonRows = lastRowCount.current;

  // First-load shell — no rows yet, still loading.
  if (isLoading && rows.length === 0) {
    return (
      <div
        data-testid="feedback-table"
        data-state="loading"
        className="rounded-md border bg-card overflow-x-auto"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-3">Time</TableHead>
              <TableHead className="px-3">Name</TableHead>
              <TableHead className="px-3">User</TableHead>
              <TableHead className="px-3">Rating</TableHead>
              <TableHead className="px-3">Feedback</TableHead>
              <TableHead className="px-3 text-right">Latency</TableHead>
              <TableHead className="px-3 text-right">Cost</TableHead>
              <TableHead className="px-3">Brief</TableHead>
              <TableHead className="px-3 w-10" aria-label="Toggle" />
            </TableRow>
          </TableHeader>
          <LoadingSkeleton colCount={9} rows={skeletonRows} />
        </Table>
      </div>
    );
  }

  // Empty state — loaded, server returned 0 rows.
  if (!isLoading && total === 0) {
    return (
      <div
        data-testid="feedback-table"
        data-state="empty"
        className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground"
      >
        {emptyLabel}
      </div>
    );
  }

  const hasMore = rows.length < total;
  const now = new Date();

  const handleRowClick = (traceId: string, e: React.MouseEvent): void => {
    // Ignore clicks on nested links / buttons so the user-profile / trace
    // links / sortable headers still work without the parent swallowing
    // them.
    const target = e.target as HTMLElement | null;
    if (target && target.closest('a, button')) return;
    toggleRow(traceId);
  };

  const handleRowKeyDown = (
    traceId: string,
    e: React.KeyboardEvent<HTMLTableRowElement>,
  ): void => {
    // Activation keys — Enter / Space (matching native button semantics).
    // Skipped when the focus landed on a nested control (a, button, …) so
    // those keep their own activation behaviour.
    const target = e.target as HTMLElement | null;
    if (target && target !== e.currentTarget && target.closest('a, button')) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      toggleRow(traceId);
    }
  };

  return (
    <div className="space-y-3">
      <div
        className="text-xs text-muted-foreground"
        data-testid="feedback-table-counter"
      >
        Showing {rows.length} of {total}
      </div>

      <div
        data-testid="feedback-table"
        data-total={total}
        className="rounded-md border bg-card overflow-x-auto"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="px-3 w-24"
                aria-sort={ariaSortFor('time', sort)}
              >
                <SortHeader
                  column="time"
                  label="Time"
                  sort={sort}
                  onSortChange={onSortChange}
                />
              </TableHead>
              <TableHead className="px-3 w-40">Name</TableHead>
              <TableHead className="px-3 w-32 hidden sm:table-cell">
                User
              </TableHead>
              <TableHead
                className="px-3 w-28"
                aria-sort={ariaSortFor('rating', sort)}
              >
                <SortHeader
                  column="rating"
                  label="Rating"
                  sort={sort}
                  onSortChange={onSortChange}
                />
              </TableHead>
              <TableHead className="px-3 hidden sm:table-cell">
                Feedback
              </TableHead>
              <TableHead
                className="px-3 w-20 text-right hidden sm:table-cell"
                aria-sort={ariaSortFor('latency', sort)}
              >
                <SortHeader
                  column="latency"
                  label="Latency"
                  sort={sort}
                  onSortChange={onSortChange}
                  align="right"
                />
              </TableHead>
              <TableHead
                className="px-3 w-20 text-right hidden sm:table-cell"
                aria-sort={ariaSortFor('cost', sort)}
              >
                <SortHeader
                  column="cost"
                  label="Cost"
                  sort={sort}
                  onSortChange={onSortChange}
                  align="right"
                />
              </TableHead>
              <TableHead className="px-3 w-16 hidden sm:table-cell">
                Brief
              </TableHead>
              <TableHead className="px-3 w-10" aria-label="Toggle" />
            </TableRow>
          </TableHeader>

          <TableBody>
            {rows.map((row) => {
              const isExpanded = expandedSet.has(row.traceId);
              const ts = new Date(row.timestamp);
              const hasRating = row.rating != null;
              const lowRating = hasRating && row.rating! <= 2;
              const inPrev = row.inputPreview;
              const outPrev = row.outputPreview;
              return (
                <React.Fragment key={row.traceId}>
                  <TableRow
                    data-testid="feedback-row"
                    data-trace-id={row.traceId}
                    data-rating={row.rating ?? ''}
                    data-expanded={isExpanded ? 'true' : 'false'}
                    data-low-rating={lowRating ? 'true' : undefined}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    aria-controls={
                      isExpanded ? `feedback-expand-${row.traceId}` : undefined
                    }
                    onClick={(e) => handleRowClick(row.traceId, e)}
                    onKeyDown={(e) => handleRowKeyDown(row.traceId, e)}
                    className={cn(
                      'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      lowRating && 'bg-red-500/5 border-l-2 border-red-500',
                      isExpanded && 'bg-muted/40',
                    )}
                  >
                    {/* Time */}
                    <TableCell className="px-3 whitespace-nowrap align-top">
                      <span title={formatDateTimeFull(ts)}>
                        {relativeTime(now, ts)}
                      </span>
                    </TableCell>

                    {/* Name + mobile previews. The Feedback column is
                        `hidden sm:table-cell`, so on narrow viewports we
                        surface the rating-feedback text and the input /
                        output snippets right under the name. Phase 5: input /
                        output previews come from the server's
                        `inputPreview` / `outputPreview` (queries.ts
                        `toPreview`), already truncated. */}
                    <TableCell className="px-3 align-top">
                      <UserLabel
                        name={row.realName}
                        username={row.userId}
                        variant="cell"
                      />
                      <div className="sm:hidden mt-1 space-y-1 text-xs">
                        {row.textFeedback ? (
                          <div
                            className="text-muted-foreground line-clamp-2 italic"
                            data-testid="feedback-row-mobile-preview"
                          >
                            &ldquo;{row.textFeedback}&rdquo;
                          </div>
                        ) : null}
                        {inPrev ? <PreviewLine label="in" text={inPrev} /> : null}
                        {outPrev ? <PreviewLine label="out" text={outPrev} /> : null}
                      </div>
                    </TableCell>

                    {/* User — hidden below sm. */}
                    <TableCell className="px-3 whitespace-nowrap hidden sm:table-cell align-top">
                      {row.userId ? (
                        <Link
                          to={`/user/${row.userId}`}
                          className="text-primary hover:underline truncate max-w-[8rem] inline-block align-middle"
                          onClick={(e) => e.stopPropagation()}
                        >
                          @{row.userId}
                        </Link>
                      ) : (
                        <span className="italic text-muted-foreground">
                          anonymous
                        </span>
                      )}
                    </TableCell>

                    {/* Rating */}
                    <TableCell className="px-3 whitespace-nowrap align-top">
                      <span
                        className={cn(
                          'font-mono tracking-tight',
                          lowRating
                            ? 'text-red-600 dark:text-red-400 font-semibold'
                            : 'text-foreground',
                        )}
                        aria-label={
                          hasRating ? `rating ${row.rating}` : 'no rating'
                        }
                      >
                        {formatStars(row.rating)}
                      </span>
                      <span
                        className={cn(
                          'ml-1 text-[11px]',
                          lowRating
                            ? 'text-red-600 dark:text-red-400 font-semibold'
                            : 'text-muted-foreground',
                        )}
                      >
                        {hasRating ? `${row.rating}/5` : ''}
                      </span>
                    </TableCell>

                    {/* Feedback (desktop) — rating-feedback text + Phase 5
                        input/output preview snippets. Server-truncated
                        already; we just stack them. The cell stays
                        max-w-[28rem] so a 200-char preview clamps to 1-2
                        lines without pushing the row taller than necessary. */}
                    <TableCell className="px-3 max-w-[28rem] hidden sm:table-cell align-top">
                      <div className="space-y-1">
                        {row.textFeedback ? (
                          <FeedbackTextPreview text={row.textFeedback} />
                        ) : !inPrev && !outPrev ? (
                          <span className="italic text-muted-foreground">
                            no text
                          </span>
                        ) : null}
                        {inPrev ? <PreviewLine label="in" text={inPrev} /> : null}
                        {outPrev ? <PreviewLine label="out" text={outPrev} /> : null}
                      </div>
                    </TableCell>

                    {/* Latency */}
                    <TableCell className="px-3 text-right tabular-nums hidden sm:table-cell align-top">
                      {formatLatency(row.latencyMs)}
                    </TableCell>

                    {/* Cost */}
                    <TableCell className="px-3 text-right tabular-nums hidden sm:table-cell align-top">
                      {formatTraceCost(row.cost)}
                    </TableCell>

                    {/* Brief */}
                    <TableCell className="px-3 hidden sm:table-cell align-top">
                      {row.isBrief ? (
                        <span
                          data-testid="feedback-row-brief-badge"
                          className="inline-flex items-center rounded bg-blue-100 text-blue-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide dark:bg-blue-950 dark:text-blue-200"
                        >
                          Brief
                        </span>
                      ) : null}
                    </TableCell>

                    {/* Toggle column — chevron + external Langfuse link. The
                        chevron is a hint, not a click target (the whole row
                        is the click target). The Langfuse link stops
                        propagation so it opens the trace in a new tab
                        without expanding the row. */}
                    <TableCell className="px-3 align-top w-10">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={langfuseTraceUrl(row.traceId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary hover:underline"
                          data-testid="feedback-row-trace-link"
                          title="Open in Langfuse"
                          aria-label="Open in Langfuse"
                        >
                          <ExternalLink
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        </a>
                        <span
                          data-testid="feedback-row-chevron"
                          aria-hidden="true"
                          className="text-muted-foreground"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>

                  {isExpanded && (
                    <TableRow
                      data-testid="feedback-row-expanded"
                      data-trace-id={row.traceId}
                      className="bg-muted/20"
                    >
                      <TableCell
                        colSpan={9}
                        className="px-3 py-3"
                        id={`feedback-expand-${row.traceId}`}
                        onKeyDown={(e) => {
                          // Esc inside the panel collapses just this row,
                          // matching the row-level Enter/Space toggle. We
                          // listen on the cell so nested children (the
                          // markdown body, the metadata accordion …) all
                          // bubble up to the same handler.
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            collapseRow(row.traceId);
                          }
                        }}
                      >
                        <RowExpandErrorBoundary
                          traceId={row.traceId}
                          onClose={() => collapseRow(row.traceId)}
                        >
                          <RowExpandPanel
                            traceId={row.traceId}
                            feedback={
                              row.rating != null || row.textFeedback
                                ? {
                                    rating: row.rating,
                                    text: row.textFeedback,
                                  }
                                : null
                            }
                            inputPreview={row.inputPreview}
                            outputPreview={row.outputPreview}
                            timestamp={row.timestamp}
                            durationMs={row.latencyMs}
                            cost={row.cost}
                            isBrief={row.isBrief}
                          />
                        </RowExpandErrorBoundary>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-center pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onLoadMore}
          disabled={!hasMore || isFetchingMore}
          data-testid="feedback-table-load-more"
        >
          {isFetchingMore ? 'Loading…' : hasMore ? 'Load more' : 'All loaded'}
        </Button>
      </div>
    </div>
  );
}
